import {useGetTopGames} from "@hooks/gamesList/useGetTopGames";
import React from "react";
import {Trans, useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {Carousel, CarouselGameCard, normalizeCarouselGame} from "@/components";
import {useAuthStore} from "@/store/useAuth.store";
import {useGameMetadataStore} from "@/store/useGameMetadataStore";
import {getProviderDisplayName} from "@/util/providerNames";
import styles from "./top-games.styles.module.scss";

const LOADING_CARD_KEYS = [
  "top-loading-0",
  "top-loading-1",
  "top-loading-2",
  "top-loading-3",
  "top-loading-4",
  "top-loading-5",
  "top-loading-6",
  "top-loading-7",
  "top-loading-8",
  "top-loading-9",
];

export const TopGames = ({provider}) => {
  const {t} = useTranslation();
  const {data} = useGetTopGames(provider && provider !== "all" ? {provider, limit: 10} : {limit: 10});
  const navigate = useNavigate();
  const authStatus = useAuthStore(state => state.authStatus);
  const handleShowAuthModal = useAuthStore(state => state.handleShowAuthModal);

  const isProviderPage = provider && provider !== "all";
  const providerDisplayName = isProviderPage ? getProviderDisplayName(provider).toUpperCase() : "";
  const initializeGames = useGameMetadataStore(state => state.initializeGames);
  const getGameById = useGameMetadataStore(state => state.getGameById);
  const gamesLoading = useGameMetadataStore(state => state.loading);
  const gamesInitialized = useGameMetadataStore(state => state.initialized);
  const [carouselApi, setCarouselApi] = React.useState(null);

  React.useEffect(() => {
    if (!gamesInitialized && !gamesLoading) {
      initializeGames();
    }
  }, [gamesInitialized, gamesLoading, initializeGames]);

  const topGames = React.useMemo(() => {
    const listTopGames = data?.data?.data || [];

    if (gamesLoading || !gamesInitialized) {
      return [];
    }

    return listTopGames.map(topGame => {
      const game = getGameById(topGame?.game_id, topGame?.game_provider);

      return normalizeCarouselGame(
        {
          ...topGame,
          id: game?.id || topGame?.game_id || topGame?.id,
          provider: game?.provider || topGame?.game_provider,
          provider_display_name: game?.provider_display_name || topGame?.provider_display_name,
          name: game?.name || topGame?.game_name,
          icon: game?.icon,
        },
        getGameById
      );
    });
  }, [data, getGameById, gamesLoading, gamesInitialized]);

  const handleGameClick = React.useCallback(
    (provider, gameId) => {
      if (authStatus === "authenticated") {
        navigate(`/games/${provider}/${gameId}`);
      } else {
        handleShowAuthModal();
      }
    },
    [authStatus, navigate, handleShowAuthModal]
  );

  const isLoading = gamesLoading || !gamesInitialized || topGames.length === 0;

  const handleCarouselInit = React.useCallback(api => {
    setCarouselApi(api);
    api?.scrollTo?.(0, true);
  }, []);

  React.useEffect(() => {
    if (isLoading) return;
    carouselApi?.scrollTo?.(0, true);
  }, [isLoading, topGames, carouselApi]);

  return (
    <div
      id="topGames"
      className={styles.topGamCon}
    >
      <div className={styles.topGamHeaBox}>
        <div className={styles.topGamTitRow}>
          <div className={styles.topGamTitBox}>
            <Trans
              i18nKey={isProviderPage ? "top10GamesSubTitleMobileProvider" : "top10GamesSubTitleMobile"}
              values={isProviderPage ? {provider: providerDisplayName} : undefined}
              parent={props => (
                <p
                  className={styles.titHeaTex}
                  {...props}
                />
              )}
              components={{
                highlight: <span className={styles.numTop} />,
              }}
            />
          </div>
          <div className={styles.arrowGroup}>
            <button
              type="button"
              className={styles.arrowBtn}
              onClick={() => carouselApi?.scrollPrev()}
              aria-label={t("carouselPrev", {defaultValue: "Anterior"})}
            >
              <svg
                aria-hidden="true"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <button
              type="button"
              className={styles.arrowBtn}
              onClick={() => carouselApi?.scrollNext()}
              aria-label={t("carouselNext", {defaultValue: "Siguiente"})}
            >
              <svg
                aria-hidden="true"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 6 15 12 9 18" />
              </svg>
            </button>
          </div>
        </div>
        <p className={styles.subTex}>
          {isProviderPage
            ? t("top10GamesSubTitleDesktopProvider", {provider: providerDisplayName})
            : t("top10GamesSubTitleDesktop")}
        </p>
      </div>
      <Carousel
        key={isLoading ? "top-games-loading" : `top-games-ready-${topGames.length}`}
        autoScroll={false}
        options={{align: "start", startIndex: 0}}
        onInit={handleCarouselInit}
        renderItems={LOADING_CARD_KEYS.map((loadingKey, idx) => {
          if (isLoading) {
            return (
              <div key={loadingKey}>
                <CarouselGameCard loading />
              </div>
            );
          }
          const game = topGames[idx % topGames.length];
          const rank = idx + 1;
          return (
            <div key={`${game.id || idx}-${idx}`}>
              <CarouselGameCard
                rank={rank}
                name={game.name}
                icon={game.icon}
                providerLabel={game.providerLabel}
                onClick={() => handleGameClick(game.provider, game.id)}
              />
            </div>
          );
        })}
      />
    </div>
  );
};
